/**
 * Dashboard-only component list ordering for Version Settings.
 * Stored in selectedVersion.dashboardMeta.componentOrder — does not affect engine/preview.
 */

import { parseCategoryPath } from "./playEngineComponentCategoryUtils";

/** @param {string | null | undefined} parentId */
export function orderKeyForParent(parentId) {
  if (!parentId) return "root";
  return `parent:${parentId}`;
}

/** @param {string} categoryPath — full path, e.g. "UI" or "UI:Buttons" */
export function orderKeyForCategory(categoryPath) {
  return `category:${categoryPath}`;
}

/** @param {string} parentPath — parent folder path; "" for top-level category folders */
export function orderKeyForCategoryFolderSiblings(parentPath) {
  return parentPath ? `categoryFolder:${parentPath}` : "categoryFolder:root";
}

/** Mixed root list: uncategorized components + top-level category folders. */
export const ROOT_LAYOUT_SCOPE = "layout:root";

const ROOT_LAYOUT_COMPONENT_PREFIX = "c:";
const ROOT_LAYOUT_FOLDER_PREFIX = "f:";

/** @param {string} componentId */
export function rootLayoutEntryForComponent(componentId) {
  return `${ROOT_LAYOUT_COMPONENT_PREFIX}${componentId}`;
}

/** @param {string} folderName */
export function rootLayoutEntryForFolder(folderName) {
  return `${ROOT_LAYOUT_FOLDER_PREFIX}${folderName}`;
}

/** @param {string} entry */
export function parseRootLayoutEntry(entry) {
  if (typeof entry !== "string") return null;
  if (entry.startsWith(ROOT_LAYOUT_COMPONENT_PREFIX)) {
    return { type: "component", id: entry.slice(ROOT_LAYOUT_COMPONENT_PREFIX.length) };
  }
  if (entry.startsWith(ROOT_LAYOUT_FOLDER_PREFIX)) {
    return { type: "folder", id: entry.slice(ROOT_LAYOUT_FOLDER_PREFIX.length) };
  }
  return null;
}

/** Accept legacy unprefixed ids saved before c:/f: prefixes existed. */
export function parseRootLayoutEntryWithLegacy(entry, uncategorizedIds, folderNames) {
  const parsed = parseRootLayoutEntry(entry);
  if (parsed) return parsed;
  if (typeof entry !== "string" || !entry) return null;
  const uncategorizedSet = new Set(uncategorizedIds || []);
  const folderSet = new Set(folderNames || []);
  if (uncategorizedSet.has(entry)) return { type: "component", id: entry };
  if (folderSet.has(entry)) return { type: "folder", id: entry };
  return null;
}

/** @param {string[] | null | undefined} entries */
export function splitRootLayoutOrder(entries) {
  const componentIds = [];
  const folderNames = [];
  for (const entry of entries || []) {
    const parsed = parseRootLayoutEntry(entry);
    if (parsed?.type === "component") componentIds.push(parsed.id);
    if (parsed?.type === "folder") folderNames.push(parsed.id);
  }
  return { componentIds, folderNames };
}

/**
 * Build / prune the mixed root layout list. When nothing is stored yet, uncategorized
 * components come first (in componentOrder.root), then top-level folders.
 *
 * @param {string[] | null | undefined} storedOrder
 * @param {string[]} uncategorizedIds
 * @param {string[]} folderNames
 * @param {string[] | null | undefined} componentOrderRoot
 */
export function resolveRootLayoutOrder(storedOrder, uncategorizedIds, folderNames, componentOrderRoot) {
  const uncategorizedSet = new Set(uncategorizedIds);
  const folderSet = new Set(folderNames);

  let order = [];
  if (Array.isArray(storedOrder) && storedOrder.length > 0) {
    order = storedOrder
      .map((entry) => {
        const parsed = parseRootLayoutEntryWithLegacy(entry, uncategorizedIds, folderNames);
        if (!parsed) return null;
        if (parsed.type === "component" && !uncategorizedSet.has(parsed.id)) return null;
        if (parsed.type === "folder" && !folderSet.has(parsed.id)) return null;
        if (parsed.type === "component") return rootLayoutEntryForComponent(parsed.id);
        return rootLayoutEntryForFolder(parsed.id);
      })
      .filter(Boolean);
  }

  const inOrder = new Set(order);
  const sortedUncategorized = sortSiblings(
    uncategorizedIds,
    componentOrderRoot?.length ? { root: componentOrderRoot } : null,
    "root",
    (id) => id
  );

  for (const id of sortedUncategorized) {
    const entry = rootLayoutEntryForComponent(id);
    if (!inOrder.has(entry)) order.push(entry);
  }
  for (const name of folderNames) {
    const entry = rootLayoutEntryForFolder(name);
    if (!inOrder.has(entry)) order.push(entry);
  }
  return order;
}

/** @param {string[] | null | undefined} layoutOrder @param {string} oldPath @param {string} newPath */
export function remapRootLayoutOrder(layoutOrder, oldPath, newPath) {
  if (!Array.isArray(layoutOrder) || parseCategoryPath(oldPath).length !== 1) return layoutOrder;
  const oldTop = parseCategoryPath(oldPath)[0];
  const newTop = parseCategoryPath(newPath)[0];
  if (!oldTop || !newTop || oldTop === newTop) return layoutOrder;
  const oldEntry = rootLayoutEntryForFolder(oldTop);
  const newEntry = rootLayoutEntryForFolder(newTop);
  return layoutOrder.map((e) => (e === oldEntry ? newEntry : e));
}

/** @param {string[] | null | undefined} layoutOrder @param {string} categoryPath */
export function pruneRootLayoutOrder(layoutOrder, categoryPath) {
  if (!Array.isArray(layoutOrder) || parseCategoryPath(categoryPath).length !== 1) return layoutOrder;
  const entry = rootLayoutEntryForFolder(parseCategoryPath(categoryPath)[0]);
  return layoutOrder.filter((e) => e !== entry);
}

/**
 * Stable sort: IDs listed in orderMap[scopeKey] first (in that order);
 * unlisted IDs keep relative engine order and appear after listed ones.
 * No-op when orderMap or scope key is missing.
 *
 * @template T
 * @param {T[]} items
 * @param {Record<string, string[]> | null | undefined} orderMap
 * @param {string} scopeKey
 * @param {(item: T) => string} getId
 * @returns {T[]}
 */
export function sortSiblings(items, orderMap, scopeKey, getId) {
  if (!items?.length) return items || [];
  if (!orderMap || !scopeKey) return items;

  const storedOrder = orderMap[scopeKey];
  if (!Array.isArray(storedOrder) || storedOrder.length === 0) return items;

  const orderIndex = new Map(storedOrder.map((id, i) => [id, i]));
  const indexed = items.map((item, originalIndex) => ({
    item,
    originalIndex,
    id: getId(item),
  }));

  indexed.sort((a, b) => {
    const aRank = orderIndex.has(a.id) ? orderIndex.get(a.id) : 1e6 + a.originalIndex;
    const bRank = orderIndex.has(b.id) ? orderIndex.get(b.id) : 1e6 + b.originalIndex;
    return aRank - bRank;
  });

  return indexed.map((x) => x.item);
}

/**
 * Move draggedId before targetId in the sibling ID list.
 *
 * @param {string[]} currentIds
 * @param {string} draggedId
 * @param {string} targetId
 * @returns {string[]}
 */
export function reorderIds(currentIds, draggedId, targetId) {
  if (!draggedId || draggedId === targetId) return currentIds;
  const next = currentIds.filter((id) => id !== draggedId);
  const targetIdx = next.indexOf(targetId);
  if (targetIdx === -1) {
    next.push(draggedId);
    return next;
  }
  next.splice(targetIdx, 0, draggedId);
  return next;
}

/**
 * Remove IDs that no longer exist in the live component set.
 *
 * @param {Record<string, string[]>} orderMap
 * @param {Set<string>} validIds
 * @returns {Record<string, string[]>}
 */
export function pruneOrderMap(orderMap, validIds) {
  if (!orderMap) return {};
  const pruned = {};
  for (const [key, ids] of Object.entries(orderMap)) {
    if (!Array.isArray(ids)) continue;
    pruned[key] = ids.filter((id) => validIds.has(id));
  }
  return pruned;
}
